// frontend/src/services/clientApi.js
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const clientApi = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token
clientApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('client_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercepteur pour gérer le refresh token
clientApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('client_refresh_token');
        const response = await axios.post(`${API_BASE}/client/auth/refresh`, null, {
          params: { refresh_token: refreshToken }
        });
        localStorage.setItem('client_access_token', response.data.access_token);
        clientApi.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
        return clientApi(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('client_access_token');
        localStorage.removeItem('client_refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// ============ AUTH ============
export const clientLogin = (email, password) => 
  clientApi.post('/client/auth/login', { email, password });

export const clientLogout = () => 
  clientApi.post('/client/auth/logout');

// ============ DASHBOARD ============
export const getDashboardStats = () => 
  clientApi.get('/client/dashboard/stats');

export const getRecentActivity = (limit = 10) => 
  clientApi.get('/client/dashboard/recent-activity', { params: { limit } });

// ============ FUEL REQUESTS ============
export const getFuelRequests = (status = null) => 
  clientApi.get('/client/fuel/requests', { params: { status } });

export const createFuelRequest = (data) => 
  clientApi.post('/client/fuel/requests', data);

export const cancelFuelRequest = (id) => 
  clientApi.delete(`/client/fuel/requests/${id}`);

// ============ MAINTENANCE REQUESTS ============
export const getMaintenanceRequests = (status = null) => 
  clientApi.get('/client/maintenance/requests', { params: { status } });

export const createMaintenanceRequest = (data) => 
  clientApi.post('/client/maintenance/requests', data);

export const cancelMaintenanceRequest = (id) => 
  clientApi.delete(`/client/maintenance/requests/${id}`);

// ============ AIRCRAFT ============
export const getMyAircraft = () => 
  clientApi.get('/client/profile/aircraft');

// ============ HANGARS ============
export const getMyHangars = () => 
  clientApi.get('/client/hangars/');

export const getHangarDetail = (id) => 
  clientApi.get(`/client/hangars/${id}`);

export const getHangarContract = (id) => 
  clientApi.get(`/client/hangars/${id}/contract`);

// ============ INVOICES ============
export const getInvoices = (status = null, limit = 50, offset = 0) => 
  clientApi.get('/client/invoices/', { params: { status, limit, offset } });

export const getInvoiceDetail = (id) => 
  clientApi.get(`/client/invoices/${id}`);

export const getInvoiceStats = () => 
  clientApi.get('/client/invoices/stats/summary');

// ============ PROFILE ============
export const getProfile = () => 
  clientApi.get('/client/profile/');

export const updateProfile = (data) => 
  clientApi.put('/client/profile/', data);

// ============ CALENDAR ============
export const getMyEvents = (startDate, endDate) => 
  clientApi.get('/client/calendar/events', { params: { start_date: startDate, end_date: endDate } });

// ============ CHAT ============
export const getClientMessages = (limit = 100) => 
  clientApi.get('/client/chat/messages', { params: { limit } });

export const sendClientMessage = (message) => 
  clientApi.post('/client/chat/messages', { message });

// ============ NOTIFICATION PREFERENCES ============
export const getNotificationPreferences = () => 
  clientApi.get('/client/profile/notifications');

export const updateNotificationPreferences = (data) => 
  clientApi.put('/client/profile/notifications', data);